import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useGetSupplierProfilesApi } from './use-get-supplier-profiles-api';

const endpoint = 'api/v1/cms/supplier-profiles';
export const unlinkSupplierProfileEndpointIdentifier = `${endpoint}/unlink`;

type TData = Record<string, any>;
type TError = AxiosError;

type TVariables = {
  id: number;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useUnlinkSupplierProfileApi(options?: Options): ReturnType {
  const { refetch: refetchSupplierProfiles } = useGetSupplierProfilesApi({ enabled: false });
  const toast = useCustomToast();

  const mutationFn = async ({ id }: TVariables) => {
    try {
      const { data } = await http.patch(`${endpoint}/${id}/unlink`);
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${endpoint}/unlink`],
    onSuccess: () => {
      refetchSupplierProfiles();

      toast({
        description: 'Supplier has been unlinked from the profile successfully',
        status: 'success',
      });
    },
    ...options,
  });
}
